import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { WidgetConfig, WidgetStatus } from "../types/widget"
import { DEFAULT_WIDGET_CONFIG } from "../constants/widget-config"
import { getWidgetConfig } from "../services/widget"        

interface WidgetStore {
  // Store State
  isLoading: boolean
  error: string | null
  activeDevice: keyof WidgetConfig["position"]
  config: WidgetConfig

  // API Actions
  fetchConfig: () => Promise<void>

  // Icon Actions
  setIconId: (iconId: string) => void
  setIconSize: (size: number) => void
  setPrimaryColor: (color: string) => void
  setLabel: (label: string) => void
  setHeading: (heading: string) => void
  setStatus: (status: WidgetStatus) => void
  setKeyboard: (keyboard: NonNullable<WidgetConfig['keyboard']>) => void
  setModuleEnabled: (section: "color" | "content" | "navigation" | "profiles" | "pageRead" | "muteSounds", key: string, enabled: boolean) => void
  setStatementEnabled: (enabled: boolean) => void
  setStatementUrl: (url: string) => void
  setStatementDisplayInWidget: (display: boolean) => void
  setStatementGeneratedDate: (date: string) => void
  setStatementFormData: (formData: WidgetConfig["modules"]["statement"]["formData"]) => void
  setLanguage: (language: string) => void
  // Position Actions
  setPosition: (device: keyof WidgetConfig["position"], position: string) => void
  setMargin: (device: keyof WidgetConfig["margins"], side: keyof WidgetConfig["margins"]["desktop"], value: number) => void
  setActiveDevice: (device: keyof WidgetConfig["position"]) => void

  // Utility Actions
  resetToDefaults: () => void
  loadConfig: (config: Partial<WidgetConfig>) => void
}

export const useWidgetStore = create<WidgetStore>()(
  devtools(
    (set) => ({
      isLoading: true,
      error: null,
      activeDevice: "desktop",
      config: DEFAULT_WIDGET_CONFIG,

      // API Actions
      fetchConfig: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await getWidgetConfig()
          // Deep-merge with defaults so new module keys always exist
          set((state) => ({
            config: {
              ...state.config,
              ...(response as unknown as Partial<WidgetConfig>),
              keyboard: {
                ...DEFAULT_WIDGET_CONFIG.keyboard,
                ...((response as unknown as WidgetConfig).keyboard || {}),
              } as NonNullable<WidgetConfig['keyboard']>,
              modules: {
                color: {
                  ...DEFAULT_WIDGET_CONFIG.modules.color,
                  ...((response as unknown as WidgetConfig).modules?.color || {}),
                },
                content: {
                  ...DEFAULT_WIDGET_CONFIG.modules.content,
                  ...((response as unknown as WidgetConfig).modules?.content || {}),
                },
                navigation: {
                  ...DEFAULT_WIDGET_CONFIG.modules.navigation,
                  ...((response as unknown as WidgetConfig).modules?.navigation || {}),
                },
                statement: {
                  ...DEFAULT_WIDGET_CONFIG.modules.statement,
                  ...((response as unknown as WidgetConfig).modules?.statement || {}),
                },
                profiles: {
                  ...DEFAULT_WIDGET_CONFIG.modules.profiles,
                  ...((response as unknown as WidgetConfig).modules?.profiles || {}),
                },
                pageRead: {
                  ...DEFAULT_WIDGET_CONFIG.modules.pageRead,
                  ...((response as unknown as WidgetConfig).modules?.pageRead || {}),
                },
                muteSounds: {
                  ...DEFAULT_WIDGET_CONFIG.modules.muteSounds,
                  ...((response as unknown as WidgetConfig).modules?.muteSounds || {}),
                },
              },
            },
          }))
        } catch (error) {
          console.error(error);
          set({ error: "An unexpected error occurred" })
        } finally {
          set({ isLoading: false })
        }
      },

      // Icon Actions
      setIconId: (iconId: string) => 
        set((state) => ({ config: { ...state.config, iconId } }), false, "setIconId"),
      setIconSize: (iconSize: number) => 
        set((state) => ({ config: { ...state.config, iconSize } }), false, "setIconSize"),
      setPrimaryColor: (primaryColor: string) => 
        set((state) => ({ config: { ...state.config, primaryColor } }), false, "setPrimaryColor"),
      setLabel: (label: string) => 
        set((state) => ({ config: { ...state.config, label } }), false, "setLabel"),
      setHeading: (heading: string) => 
        set((state) => ({ config: { ...state.config, heading } }), false, "setHeading"),
      setStatus: (status: WidgetStatus) =>
        set((state) => ({ config: { ...state.config, status } }), false, "setStatus"),
      setKeyboard: (keyboard) =>
        set((state) => ({ config: { ...state.config, keyboard } }), false, "setKeyboard"),
      setModuleEnabled: (section, key, enabled) =>
        set(
          (state) => {
            const NESTED_SECTIONS = ['color', 'content', 'navigation'] as const;
            const FLAT_SECTIONS = ['profiles', 'pageRead', 'muteSounds'] as const;
            if ((NESTED_SECTIONS as readonly string[]).includes(section)) {
              return {
                config: {
                  ...state.config,
                  modules: {
                    ...state.config.modules,
                    [section]: {
                      ...(state.config.modules as Record<string, unknown>)[section] as Record<string, { enabled: boolean }>,
                      [key]: { enabled },
                    },
                  },
                },
              };
            }
            if ((FLAT_SECTIONS as readonly string[]).includes(section)) {
              return {
                config: {
                  ...state.config,
                  modules: {
                    ...state.config.modules,
                    [section]: { enabled },
                  },
                },
              };
            }
            return state;
          },
          false,
          "setModuleEnabled",
        ),
      setStatementEnabled: (enabled: boolean) => 
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, enabled } } } }), false, "setStatementEnabled"),
      setStatementUrl: (url: string) => 
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, url } } } }), false, "setStatementUrl"),
      setStatementDisplayInWidget: (displayInWidget: boolean) =>
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, displayInWidget } } } }), false, "setStatementDisplayInWidget"),
      setStatementGeneratedDate: (generatedDate: string) =>
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, generatedDate } } } }), false, "setStatementGeneratedDate"),
      setStatementFormData: (formData) =>
        set(
          (state) => ({
            config: {
              ...state.config,
              modules: {
                ...state.config.modules,
                statement: { ...state.config.modules.statement, formData },
              },
            },
          }),
          false,
          "setStatementFormData",
        ),
      setLanguage: (language: string) =>
        set((state) => ({ config: { ...state.config, language: { ...state.config.language, default: language } } }), false, "setLanguage"),
      // Position Actions
      setPosition: (device, position) =>
        set(
          (state) => ({
            config: {
              ...state.config,
              position: {
                ...state.config.position,
                [device]: position,
              },
            },
          }),
          false,
          "setPosition",
        ),

      setMargin: (device, side, value) =>
        set(
          (state) => ({
            config: {
              ...state.config,
              margins: {
                ...state.config.margins,
                [device]: {
                  ...state.config.margins[device],
                  [side]: value,
                },
              },
            },
          }),
          false,
          "setMargin",
        ),

      setActiveDevice: (device) =>
        set(
          { activeDevice: device },
          false,
          "setActiveDevice",
        ),

      // Utility Actions
      resetToDefaults: () => set({ config: DEFAULT_WIDGET_CONFIG }, false, "resetToDefaults"),

      loadConfig: (newConfig: Partial<WidgetConfig>) => {
        set(
          (state) => ({
            config: {
              ...state.config,
              ...newConfig,
              position: newConfig.position ? { ...state.config.position, ...newConfig.position } : state.config.position,
              margins: newConfig.margins ? { ...state.config.margins, ...newConfig.margins } : state.config.margins,
              keyboard: newConfig.keyboard
                ? { ...state.config.keyboard, ...newConfig.keyboard } as NonNullable<WidgetConfig['keyboard']>
                : state.config.keyboard,
              modules: {
                color: {
                  ...state.config.modules.color,
                  ...(newConfig.modules?.color || {}),
                },
                content: {
                  ...state.config.modules.content,
                  ...(newConfig.modules?.content || {}),
                },
                navigation: {
                  ...state.config.modules.navigation,
                  ...(newConfig.modules?.navigation || {}),
                },
                statement: {
                  ...state.config.modules.statement,
                  ...(newConfig.modules?.statement || {}),
                },
                profiles: {
                  ...state.config.modules.profiles,
                  ...(newConfig.modules?.profiles || {}),
                },
                pageRead: {
                  ...state.config.modules.pageRead,
                  ...(newConfig.modules?.pageRead || {}),
                },
                muteSounds: {
                  ...state.config.modules.muteSounds,
                  ...(newConfig.modules?.muteSounds || {}),
                },
              },
            },
          }),
          false,
          "loadConfig",
        )
      },
    }),
    { name: "widget-store" },
  ),
)

// Legacy hook for backward compatibility
export const useWidgetSettings = () => {
  const store = useWidgetStore()

  return {
    settings: {
      selectedIcon: store.config.iconId,
      iconSize: [store.config.iconSize],
      primaryColor: store.config.primaryColor,
      position: store.config.position,
      margins: store.config.margins,
      statement: store.config.modules.statement,
    },
    setters: {
      setSelectedIcon: store.setIconId,
      setIconSize: (size: number[]) => store.setIconSize(size[0]),
      setPrimaryColor: store.setPrimaryColor,
      setLineHeight: () => {},
      setDyslexicFont: () => {},
      setTextAlignment: () => {},
      setBrightness: () => {},
      setContrast: () => {},
      setSaturation: () => {},
    },
    handlers: {
      handlePositionChange: (device: string, value: string) =>
        store.setPosition(device as keyof WidgetConfig["position"], value),
      handleMarginChange: (device: string, side: string, value: number) =>
        store.setMargin(
          device as keyof WidgetConfig["margins"],
          side as keyof WidgetConfig["margins"]["desktop"],
          value
        ),
      resetToDefaults: store.resetToDefaults,
    },
  }
}
